<template>
	<div class="category-tree-container">
		<!-- 商品分类（父级） -->
		<el-skeleton :loading="categoryLoading" animated :rows="3"> </el-skeleton>

		<div class="category-filters" :class="{ 'category-filters-fixed': isCategoryFixed }">
			<div v-for="category in categories" :key="category.id" class="category-filter"
				:class="{ active: category.active, 'category-filter-fixed': isCategoryFixed }"
				@click="category.children && category.children.length > 0 ? null : selectCategory(category)"
				@mouseenter="showSubcategories(category, $event)" @mouseleave="hideSubcategories">
				{{ category.name }}
			</div>
		</div>

		<!-- 二级商品分类 (独立元素，避免被遮挡) -->
		<div v-if="showDropdown && currentCategory" class="subcategory-dropdown" :style="dropdownStyle"
			@mouseenter="keepDropdownOpen" @mouseleave="hideSubcategories">
			<div v-for="subCategory in currentCategory.children" :key="subCategory.id" class="subcategory-item"
				:class="{ active: subCategory.active }"
				@click.stop="subCategory.children && subCategory.children.length > 0 ? null : selectCategory(subCategory)"
				@mouseenter="handleSubCategoryHover(subCategory, $event)" @mouseleave="hideThirdLevel">
				{{ subCategory.name }}
			</div>
		</div>

		<!-- 三级商品分类 (显示在二级分类右边) -->
		<div v-if="showThirdLevel && currentSubCategory" class="third-level-dropdown" :style="thirdLevelStyle"
			@mouseenter="keepThirdLevelOpen" @mouseleave="hideSubcategories">
			<div v-for="thirdCategory in currentSubCategory.children" :key="thirdCategory.id" class="third-level-item"
				:class="{ active: thirdCategory.active }" @click.stop="selectCategory(thirdCategory)"
				@mouseenter="keepThirdLevelOpen">
				{{ thirdCategory.name }}
			</div>
		</div>
	</div>
</template>

<script>
	import productApi from '@/api/product'
	export default {
		name: 'CategoryTree',
		props: {
			// 分类栏是否固定
			isCategoryFixed: {
				type: Boolean,
				default: false
			}
		},
		data() {
			return {
				categoryLoading: true,
				categories: [],
				hoveredCategory: null, // 当前悬停的分类ID
				showDropdown: false, // 是否显示下拉菜单
				currentCategory: null, // 当前显示下拉菜单的分类
				dropdownStyle: {}, // 下拉菜单的样式
				dropdownTimer: null, // 用于延迟隐藏的定时器
				showThirdLevel: false, // 是否显示三级分类
				currentSubCategory: null, // 当前显示三级分类的二级分类
				thirdLevelStyle: {}, // 三级分类的样式
				thirdLevelTimer: null // 用于延迟隐藏三级分类的定时器
			}
		},
		methods: {
			//页面初始化方法
			init() {
				this.getGoodsCategory();
			},
			//获取商品分类
			async getGoodsCategory() {
				const res = await productApi.getGoodsCategory(0);
				this.categories = res.result;
				this.categoryLoading = false;
				this.categories.unshift({
					id: "all",
					name: "全部"
				});
				this.categories.map((category, index) => {
					category.active = index == 0;
					if (category.children && category.children.length != 0) {
						category.children.unshift({
							id: category.id,
							name: "全部"
						})
						category.children.forEach(subCategory => {
							subCategory.active = false;
							if (subCategory.children && subCategory.children.length != 0) {
								subCategory.children.unshift({
									id: subCategory.id,
									name: "全部"
								})
								subCategory.children.forEach(thirdCategory => {
									thirdCategory.active = false;
								});
							}
						});
					}
				})
			},
			// 选择分类
			selectCategory(category) {
				// 重置所有分类的激活状态
				this.resetCategoriesActiveState();
				// 设置当前分类为激活状态
				this.setCategoryActive(category);
				// 强制组件重新渲染，确保选中效果立即显示
				this.$forceUpdate();
				// 触发分类选择事件
				this.$emit('category-selected', category);
				// 重置悬停状态
				this.hoveredCategory = null;
				this.showDropdown = false;
				this.currentCategory = null;
				this.dropdownStyle = {};
				this.showThirdLevel = false;
				this.currentSubCategory = null;
				this.thirdLevelStyle = {};
				// 清除定时器
				if (this.dropdownTimer) {
					clearTimeout(this.dropdownTimer);
				}
				if (this.thirdLevelTimer) {
					clearTimeout(this.thirdLevelTimer);
				}
			},
			// 重置所有分类的激活状态
			resetCategoriesActiveState() {
				this.categories.forEach(category => {
					category.active = false;
					if (category.children) {
						category.children.forEach(subCategory => {
							subCategory.active = false;
							if (subCategory.children) {
								subCategory.children.forEach(thirdCategory => {
									thirdCategory.active = false;
								});
							}
						});
					}
				});
			},
			// 设置分类为激活状态，并同步设置父分类为激活状态
			setCategoryActive(category) {
				// 设置当前分类为激活状态
				category.active = true;
				// 查找并设置父分类为激活状态
				this.findAndSetParentActive(category);
			},
			// 查找并设置父分类为激活状态
			findAndSetParentActive(category) {
				// 查找二级分类的父分类
				for (const parentCategory of this.categories) {
					if (parentCategory.children) {
						for (const subCategory of parentCategory.children) {
							// 如果是三级分类，查找其父分类（二级分类）
							if (subCategory.children) {
								for (const thirdCategory of subCategory.children) {
									if (thirdCategory.id === category.id) {
										// 设置二级分类为激活状态
										subCategory.active = true;
										// 设置一级分类为激活状态
										parentCategory.active = true;
										return;
									}
								}
							}
							// 如果是二级分类，查找其父分类（一级分类）
							if (subCategory.id === category.id) {
								// 设置一级分类为激活状态
								parentCategory.active = true;
								return;
							}
						}
					}
				}
			},
			// 当鼠标移入category-filter时，隐藏现有的subcategory和third-level-dropdown
			showSubcategories(category, event) {
				// 清除之前的定时器
				if (this.dropdownTimer) {
					clearTimeout(this.dropdownTimer);
				}
				if (this.thirdLevelTimer) {
					clearTimeout(this.thirdLevelTimer);
				}
				// 隐藏现有的下拉菜单
				this.showDropdown = false;
				this.currentCategory = null;
				this.dropdownStyle = {};
				this.showThirdLevel = false;
				this.currentSubCategory = null;
				this.thirdLevelStyle = {};
				// 如果当前分类有子分类，显示新的下拉菜单
				if (category.children && category.children.length > 0) {
					// 设置当前分类
					this.currentCategory = category;
					this.showDropdown = true;
					// 计算下拉菜单的位置
					if (event) {
						const target = event.currentTarget;
						const rect = target.getBoundingClientRect();
						this.dropdownStyle = {
							position: 'fixed',
							left: `${rect.left}px`,
							top: `${rect.bottom}px`,
							zIndex: '9999',
							minWidth: `${rect.width}px`
						};
					}
				}
			},
			// 隐藏二级分类和三级分类
			hideSubcategories() {
				// 使用setTimeout延迟隐藏，以便鼠标可以移动到子分类上
				this.dropdownTimer = setTimeout(() => {
					// 隐藏二级分类
					this.showDropdown = false;
					this.currentCategory = null;
					this.dropdownStyle = {};
					// 同时隐藏三级分类
					this.showThirdLevel = false;
					this.currentSubCategory = null;
					this.thirdLevelStyle = {};
				}, 200);
			},
			// 保持下拉菜单打开
			keepDropdownOpen() {
				// 清除延迟隐藏的定时器
				if (this.dropdownTimer) {
					clearTimeout(this.dropdownTimer);
				}
				if (this.thirdLevelTimer) {
					clearTimeout(this.thirdLevelTimer);
				}
			},
			// 处理二级分类悬停
			handleSubCategoryHover(subCategory, event) {
				// 清除之前的定时器
				if (this.thirdLevelTimer) {
					clearTimeout(this.thirdLevelTimer);
				}
				if (this.dropdownTimer) {
					clearTimeout(this.dropdownTimer);
				}

				// 检查是否有子分类
				if (subCategory.children && subCategory.children.length > 0) {
					this.currentSubCategory = subCategory;
					this.showThirdLevel = true;

					// 计算三级分类的位置
					if (event) {
						const target = event.currentTarget;
						const rect = target.getBoundingClientRect();
						this.thirdLevelStyle = {
							position: 'fixed',
							left: `${rect.right}px`,
							top: `${rect.top}px`,
							zIndex: '9999',
							minWidth: '200px'
						};
					}
				} else {
					// 如果没有子分类，隐藏三级分类
					this.showThirdLevel = false;
					this.currentSubCategory = null;
					this.thirdLevelStyle = {};
				}
			},
			// 隐藏三级分类
			hideThirdLevel() {
				// 使用setTimeout延迟隐藏，以便鼠标可以移动到三级分类上
				this.thirdLevelTimer = setTimeout(() => {
					this.showThirdLevel = false;
					this.currentSubCategory = null;
					this.thirdLevelStyle = {};
				}, 200);
			},
			// 保持三级分类打开
			keepThirdLevelOpen() {
				// 清除延迟隐藏的定时器
				if (this.thirdLevelTimer) {
					clearTimeout(this.thirdLevelTimer);
				}
				if (this.dropdownTimer) {
					clearTimeout(this.dropdownTimer);
				}
			}
		}
	}
</script>

<style scoped>
	/* Category Filters */
	.category-filters {
		display: flex;
		margin-bottom: 30px;
		flex-wrap: nowrap;
		overflow-x: auto;
		padding: 10px 0;
		/* 默认隐藏滚动条 */
		scrollbar-width: thin;
		scrollbar-color: transparent transparent;
		position: relative;
		z-index: 10;
	}

	/* 鼠标移入时显示滚动条 */
	.category-filters:hover {
		scrollbar-color: #ccc transparent;
	}

	/* WebKit浏览器滚动条样式 */
	.category-filters::-webkit-scrollbar {
		width: 6px;
		height: 6px;
	}

	.category-filters::-webkit-scrollbar-track {
		background: transparent;
	}

	.category-filters::-webkit-scrollbar-thumb {
		background-color: transparent;
		border-radius: 3px;
	}

	.category-filters:hover::-webkit-scrollbar-thumb {
		background-color: #ccc;
	}

	.category-filter {
		padding: 20px 33px;
		background-color: #f5f5f5;
		color: #333;
		cursor: pointer;
		font-size: 17px;
		transition: all 0.3s ease;
		flex-shrink: 0;
		white-space: nowrap;
		position: relative;
		z-index: 9998 !important;
		/* 确保分类过滤器不会裁剪下拉菜单 */
		overflow: visible !important;
	}
	
	.category-filter-fixed {
		padding: 20px 36px;
	}

	.category-filter.active {
		background-color: #016AF8;
		font-weight: bold;
		color: white;
	}

	.category-filter:hover {
		background-color: #E6F1FE;
		color: #016AF8;
	}

	.category-filter.active:hover {
		background-color: #0156c8;
		color: #fff;
	}

	/* 分类箭头 */
	.category-arrow {
		font-size: 12px;
		margin-left: 5px;
		transition: transform 0.3s ease;
	}

	.category-filter:hover .category-arrow {
		transform: rotate(180deg);
	}

	/* 二级分类下拉菜单 */
	.subcategory-dropdown {
		background-color: white;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
		border-radius: 0 0 4px 4px;
		margin-top: 0;
		padding: 0;
		/* 确保没有滚动条 */
		overflow: visible;
		/* 确保下拉菜单不会被截断 */
		white-space: normal;
		/* 确保下拉菜单的大小不会影响其他元素 */
		max-height: 500px;
		/* 防止下拉菜单被其他元素遮挡 */
		backface-visibility: hidden;
		/* 确保下拉菜单显示在最顶层 */
		pointer-events: auto;
	}

	.subcategory-item {
		padding: 12px 20px;
		cursor: pointer;
		transition: all 0.3s ease;
		white-space: nowrap;
		/* 确保子分类项不产生滚动条 */
		overflow: hidden;
		text-overflow: ellipsis;
		/* 确保子分类项有足够的空间显示箭头 */
		position: relative;
		padding-right: 30px;
	}

	.subcategory-item:hover {
		background-color: #E6F1FE;
		color: #016AF8;
	}

	.subcategory-item.active {
		background-color: #016AF8;
		font-weight: bold;
		color: white;
	}

	.subcategory-item.active:hover {
		background-color: #0156c8;
		color: #fff;
	}

	/* 二级分类箭头 */
	.subcategory-arrow {
		position: absolute;
		right: 15px;
		top: 50%;
		transform: translateY(-50%);
		font-size: 12px;
		color: #999;
	}

	.subcategory-item:hover .subcategory-arrow {
		color: #016AF8;
	}

	/* 三级分类下拉菜单 */
	.third-level-dropdown {
		background-color: white;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
		border-radius: 4px;
		margin-top: 0;
		padding: 0;
		/* 确保没有滚动条 */
		overflow: visible;
		/* 确保下拉菜单不会被截断 */
		white-space: normal;
		/* 确保下拉菜单的大小不会影响其他元素 */
		max-height: 500px;
		/* 防止下拉菜单被其他元素遮挡 */
		backface-visibility: hidden;
		/* 确保下拉菜单显示在最顶层 */
		pointer-events: auto;
	}

	.third-level-item {
		padding: 12px 20px;
		cursor: pointer;
		transition: all 0.3s ease;
		white-space: nowrap;
		/* 确保子分类项不产生滚动条 */
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.third-level-item:hover {
		background-color: #E6F1FE;
		color: #016AF8;
	}

	.third-level-item.active {
		background-color: #016AF8;
		font-weight: bold;
		color: white;
	}

	.third-level-item.active:hover {
		background-color: #0156c8;
		color: #fff;
	}

	/* 固定分类栏样式 */
	.category-filters-fixed {
		position: fixed !important;
		top: 60px !important;
		left: 0;
		right: 0;
		z-index: 999;
		background-color: #f2f2f2;
		padding: 0 !important;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
		justify-content: flex-start;
		overflow-x: auto;
		justify-content: center;
		/* 默认隐藏滚动条 */
		scrollbar-width: thin;
		scrollbar-color: transparent transparent;
		/* 确保固定分类栏不会遮挡下拉菜单 */
		overflow: visible !important;
		z-index: 9998 !important;
	}

	/* 鼠标移入时显示滚动条 */
	.category-filters-fixed:hover {
		scrollbar-color: #ccc transparent;
	}

	/* WebKit浏览器滚动条样式 */
	.category-filters-fixed::-webkit-scrollbar {
		width: 6px;
		height: 6px;
	}

	.category-filters-fixed::-webkit-scrollbar-track {
		background: transparent;
	}

	.category-filters-fixed::-webkit-scrollbar-thumb {
		background-color: transparent;
		border-radius: 3px;
	}

	.category-filters-fixed:hover::-webkit-scrollbar-thumb {
		background-color: #ccc;
	}

	/* 固定分类栏下的子分类样式调整 */
	.category-filters-fixed .category-filter {
		position: relative;
		z-index: 9999;
	}

	/* 响应式调整 */
	@media (max-width: 768px) {
		.category-filters {
			gap: 10px;
			padding: 5px 0;
		}

		.category-filter {
			padding: 6px 12px;
			font-size: 12px;
		}
	}
</style>